// Client dashboard v2.1 (BOT-451). Days are ET calendar days ("YYYY-MM-DD"),
// computed by the API; timestamps are UTC ISO.

import { getInit, type PollOptions } from './poll';

export type QueueKey = 'price' | 'home_value' | 'new_items';

export interface SeriesPoint {
  day: string;
  /** null = no snapshot that day (a gap), 0 = an empty queue. */
  pending: number | null;
}

export interface QueueTrend {
  direction: 'growing' | 'stable' | 'shrinking';
  delta: number;
  /** Days between the first stored point and today (30 once history is full). */
  days: number;
}

interface TileBase {
  pending: number;
  series: SeriesPoint[];
  trend: QueueTrend | null;
}

export interface DashboardReview {
  as_of: string;
  waiting_total: number;
  tiles: {
    price: TileBase & { unsafe: number; oldest_days: number | null };
    home_value: TileBase & { shorter: number; longer: number; first_value: number };
    new_items: TileBase & { auto_skipped: number };
  };
  by_vendor: {
    price: (VendorBar & { safe: number; unsafe: number })[];
    home_value: (VendorBar & { shorter: number; longer: number; first_value: number })[];
    new_items: VendorBar[];
  };
}

export interface VendorBar {
  /** Display label from the API's vendor map — never an internal code. */
  label: string;
  /** Codes the Review page filters on (`?vendor=…`); not for display. */
  vendor_codes: string[];
  pending: number;
}

export interface ActivitySeries {
  automatic: number[];
  manual: number[];
  automatic_total: number;
  manual_total: number;
  total: number;
}

export interface DashboardActivity {
  as_of: string;
  days: string[];
  price: ActivitySeries;
  home_value: ActivitySeries;
  new_items: ActivitySeries;
}

export async function fetchDashboardReview(opts: PollOptions = {}): Promise<DashboardReview> {
  const res = await fetch('/api/v1/dashboard/review', getInit(opts));
  if (!res.ok) throw new Error(`Dashboard review failed: ${res.status}`);
  return res.json() as Promise<DashboardReview>;
}

export async function fetchDashboardActivity(opts: PollOptions = {}): Promise<DashboardActivity> {
  const res = await fetch('/api/v1/dashboard/activity', getInit(opts));
  if (!res.ok) throw new Error(`Dashboard activity failed: ${res.status}`);
  return res.json() as Promise<DashboardActivity>;
}

// ── Data freshness (Phase B) ────────────────────────────────────────────────

/** Age buckets, then "vendor shows no price" / "vendor gives no stock info", then never checked. */
export type FreshnessSegmentKey =
  | 'd0_7' | 'd8_14' | 'd15_30' | 'd31_60' | 'd61_90' | 'd91_180' | 'd181_365' | 'over_1y'
  | 'no_price' | 'no_stock_info' | 'never';

export interface FreshnessColumn {
  total: number;
  fresh: number;
  fresh_pct: number | null;
  /** Items not confirmed within 30 days, never-checked included. */
  older: number;
  segments: { key: FreshnessSegmentKey; items: number }[];
  history_from: string | null;
  by_source: { label: string; fresh: number; confirmed: number }[];
}

export interface FreshnessVendor {
  label: string;
  total: number;
  price: FreshnessColumn;
  /** null = the vendor has no Home Value source. */
  home_value: FreshnessColumn | null;
}

export interface DashboardFreshness {
  as_of: string;
  price: {
    computed_at: string | null;
    fresh_pct: number | null;
    backfill: { done: boolean; through: string | null };
  };
  home_value: { computed_at: string | null; fresh_pct: number | null };
  /** Least fresh first. */
  vendors: FreshnessVendor[];
}

export async function fetchDashboardFreshness(opts: PollOptions = {}): Promise<DashboardFreshness> {
  const res = await fetch('/api/v1/dashboard/freshness', getInit(opts));
  if (!res.ok) throw new Error(`Dashboard freshness failed: ${res.status}`);
  return res.json() as Promise<DashboardFreshness>;
}

// ── Data sources ────────────────────────────────────────────────────────────

export type SourceState =
  | 'running' | 'processing_file' | 'paused' | 'stopped' | 'idle'
  | 'waiting_file' | 'scheduled' | 'turned_off' | 'blocked';

export type SourceCause =
  | 'site_timeout' | 'bot_protection' | 'errors' | 'cant_sign_in' | 'not_responding' | 'missed_run'
  | 'login_limit' | 'daily_limit' | 'no_new_work' | 'pass_completed' | 'file_overdue';

export type SourceEta =
  | { kind: 'days'; days: number }
  | { kind: 'completed'; completed_at: string | null }
  | { kind: 'unknown_stopped' | 'turned_off' | 'unknown' | 'over_3_months' };

export interface DataSource {
  /** Display label from the API's source map — never an internal code. */
  label: string;
  feeds: ('price' | 'home_value')[];
  kind: 'continuous' | 'file' | 'slot';
  status: {
    state: SourceState;
    cause: SourceCause | null;
    since: string | null;
    resumes_at: string | null;
    last_result_at: string | null;
    next_run_at: string | null;
    /** CDK lanes: the price file's date ("YYYY-MM-DD") and the next one expected. */
    cdk_file_date: string | null;
    next_file_date: string | null;
  };
  /** The last unplanned stop, for a day after the source came back. */
  last_stop: { stopped_at: string | null; resumed_at: string; cause: SourceCause | null } | null;
  pass: {
    done: number;
    total: number;
    pct: number | null;
    since: string | null;
    /** Latest status of the checked items; a retried Error moves to OK / Not found. */
    by_status: { ok: number; not_found: number; error: number; no_data: number };
  };
  eta: SourceEta;
}

export interface DashboardSources {
  as_of: string;
  computed_at: string | null;
  /** Problems first. */
  sources: DataSource[];
}

export async function fetchDashboardSources(opts: PollOptions = {}): Promise<DashboardSources> {
  const res = await fetch('/api/v1/dashboard/sources', getInit(opts));
  if (!res.ok) throw new Error(`Dashboard sources failed: ${res.status}`);
  return res.json() as Promise<DashboardSources>;
}
